import { ArrowLeft, Award, ChevronDown, School, Sparkles, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import class10Results2025 from '../data/class10Results2025.json';
import '../styles/Class10Results2025.css';

const SUBJECT_LABELS = { maths: 'Maths', science: 'Science', socialScience: 'SST' };
const SUBJECT_ORDER = ['maths', 'science', 'socialScience'];

const getHighestMark = (marks) =>
  Math.max(...Object.values(marks).filter((mark) => mark !== null && mark !== undefined));

const rankedResults = [...class10Results2025].sort(
  (left, right) =>
    getHighestMark(right.marks) - getHighestMark(left.marks) ||
    right.averageMarks - left.averageMarks ||
    right.totalMarks - left.totalMarks ||
    left.fullName.localeCompare(right.fullName),
);

const formatMark = (mark) => (Number.isInteger(mark) ? String(mark) : mark.toFixed(0));

const getVisibleMarks = (marks) =>
  Object.entries(marks)
    .filter(([, value]) => value !== null && value !== undefined)
    .sort(
      ([leftSubject, leftValue], [rightSubject, rightValue]) =>
        rightValue - leftValue ||
        SUBJECT_ORDER.indexOf(leftSubject) - SUBJECT_ORDER.indexOf(rightSubject),
    )
    .map(([subject, value]) => ({ subject, label: SUBJECT_LABELS[subject], value: formatMark(value) }));

function Marks({ student }) {
  return (
    <div className="class10-marks-grid">
      {getVisibleMarks(student.marks).map((mark) => (
        <div key={`${student.id}-${mark.subject}`} className={`class10-mark-chip class10-mark-chip-${mark.subject}`}>
          <span>{mark.label}</span>
          <strong>{mark.value}</strong>
        </div>
      ))}
    </div>
  );
}

function StudentCard({ student, rank }) {
  return (
    <article className="class10-student-card">
      <span className="class10-card-rank" aria-label={`Rank ${rank}`}>{String(rank).padStart(2, '0')}</span>
      <div className="class10-card-profile">
        <img src={student.passportImage} alt={`${student.fullName} passport`} className="class10-card-photo" loading="lazy" decoding="async" />
        <div className="class10-card-copy">
          <h3>{student.fullName}</h3>
          <p><School size={15} /><span>{student.schoolName}</span></p>
        </div>
      </div>
      <Marks student={student} />
    </article>
  );
}

function FeaturedStudent({ student, rank }) {
  return (
    <article className={`class10-featured-card class10-featured-card-${rank}`}>
      <div className="class10-featured-topline">
        <span>#{String(rank).padStart(2, '0')}</span>
        {rank === 1 && <Trophy size={19} aria-label="Top rank" />}
      </div>
      <img src={student.passportImage} alt={`${student.fullName} passport`} className="class10-featured-photo" />
      <div className="class10-featured-copy">
        <p className="class10-featured-eyebrow">{rank === 1 ? 'Highest achiever' : 'Top performer'}</p>
        <h3>{student.fullName}</h3>
        <p className="class10-featured-school">{student.schoolName}</p>
      </div>
      <Marks student={student} />
    </article>
  );
}

function Class10Results2025() {
  const [firstPlace, secondPlace, thirdPlace] = rankedResults;
  const remainingResults = rankedResults.slice(3);

  return (
    <main className="class10-results-page">
      <section className="class10-results-hero">
        <div className="class10-hero-orb class10-hero-orb-one" />
        <div className="class10-hero-orb class10-hero-orb-two" />
        <div className="class10-hero-inner">
          <Link to="/" className="class10-back-link"><ArrowLeft size={16} /> Back to home</Link>
          <div className="class10-hero-content">
            <div>
              <p className="class10-kicker"><Sparkles size={16} /> Celebration of excellence</p>
              <h1>Class 10th<br /><em>2025-26 Toppers</em></h1>
              <p className="class10-hero-description">2025 results that reflect commitment, curiosity, and the confidence to go further.</p>
            </div>
          </div>
          <div className="class10-result-seal"><Award size={25} /><strong>100%</strong><span>Result</span></div>
          <div className="class10-hero-footer">
            <a href="#honour-roll">Explore toppers <ChevronDown size={17} /></a>
          </div>
        </div>
      </section>

      <section className="class10-spotlight" aria-labelledby="spotlight-title">
        <div className="class10-section-intro">
          <p>Spotlight</p><h2 id="spotlight-title">Leading the way</h2>
        </div>
        <div className="class10-featured-grid">
          <FeaturedStudent student={secondPlace} rank={2} />
          <FeaturedStudent student={firstPlace} rank={1} />
          <FeaturedStudent student={thirdPlace} rank={3} />
        </div>
      </section>

      <section id="honour-roll" className="class10-honour-roll" aria-label="All Class 10th toppers">
        <div className="class10-results-grid">
          {remainingResults.map((student, index) => <StudentCard key={student.id} student={student} rank={index + 4} />)}
        </div>
      </section>
    </main>
  );
}

export default Class10Results2025;
